import { IValidationErrorResponse, IServerErrorResponse } from '../types';

const fieldErrorKeys = {
  password: {
    length: 'membersErrorPasswordLength',
  },
  email: {
    format: 'membersErrorEmailFormat',
  },
};

export interface ServerErrorResult {
  errorKey: string;
  errorCode: string | undefined;
}

export const serverErrorsHandler = (
  error: IValidationErrorResponse | IServerErrorResponse | string | number,
): ServerErrorResult => {
  if (typeof error === 'object') {
    return handleObjectError(error);
  }
  const errorStr = error.toString().replace('-', '');
  return {
    errorKey: `membersServerError_${errorStr}`,
    errorCode: errorStr,
  };
};

type TranslateFn = (
  key: string,
  options?: { defaultValue?: string } & Record<string, unknown>,
) => string;

/**
 * Resolves a server error into a user-facing message: maps the error to its
 * `membersServerError_<code>` key and falls back to the generic message when no
 * specific translation exists. Shared by the login flows (email/password and
 * social) so a known server code such as NO_EMAIL_WAS_PROVIDED_FROM_IDP shows
 * its specific message instead of the generic one.
 *
 * `interpolations` are forwarded to the translator for its `{var}` placeholders
 * (this app's i18n runs in ICU mode — single braces, like `{platformName}` in
 * the social button labels; e.g. the social provider name in
 * NO_EMAIL_WAS_PROVIDED_FROM_IDP). Unused values are ignored, so passing them
 * for every error is safe.
 */
export const resolveServerErrorMessage = (
  error: IValidationErrorResponse | IServerErrorResponse | string | number,
  t: TranslateFn,
  interpolations?: Record<string, unknown>,
): string => {
  const { errorKey } = serverErrorsHandler(error);
  return t(errorKey, {
    ...interpolations,
    defaultValue: t('membersErrorGeneral'),
  });
};

const handleObjectError = (error: any): ServerErrorResult => {
  const details = error.details;
  const validationError = details?.validationError?.fieldViolations[0];

  // Email format validation error
  if (validationError?.data?.type === 'EMAIL') {
    return {
      errorKey: fieldErrorKeys.email.format,
      errorCode: validationError.ruleName || 'EMAIL_FORMAT',
    };
  }

  // Password length validation error
  if (
    validationError?.field === 'password' &&
    validationError?.ruleName === 'VALUE_TOO_SHORT'
  ) {
    return {
      errorKey: fieldErrorKeys.password.length,
      errorCode: validationError.ruleName,
    };
  }

  // Other validation errors with rule names
  if (validationError?.ruleName) {
    const ruleName = validationError.ruleName;
    return {
      errorKey: `membersError${ruleName}`,
      errorCode: ruleName,
    };
  }

  // Application or system errors with error codes
  const code = details?.errorcode || details?.applicationError?.code;
  if (code) {
    const normalizedCode = code.toString().replace('-', '');
    return {
      errorKey: `membersServerError_${normalizedCode}`,
      errorCode: normalizedCode,
    };
  }

  // Fallback to generic error
  return {
    errorKey: 'membersErrorGeneral',
    errorCode: undefined,
  };
};
